/* eslint-disable max-len */
/* eslint-disable no-undef */
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import {
  toWidget,
  viewToModelPositionOutsideModelElement
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import NumberedCitationCommand from './numberedcitationcommand';
import { removeDefaultHtmlSpanAttributes } from '../../src/utils/subtemplateutils';
import { CkeditorConstants } from '../../src/utils/ckeditorconstants';

const {
  htmlElement: { SPAN, LINK_SPAN },
  class: { REF },
  htmlAttributes: { NUMBERED_CITATION_CLASS, CLASS },
  helperAttributes: { NUMBERED_CITATION, CITATION_COUNT },
  trackchangesMarkup: { SUGGESTION_END_AFTER, SUGGESTION_START_AFTER, SUGGESTION_END_BEFORE, SUGGESTION_START_BEFORE },
  NUM_REF_POINTER_MARKUP } = CkeditorConstants;

export default class NumberedCitationEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();
    this.editor.commands.add(NUMBERED_CITATION, new NumberedCitationCommand(this.editor));
    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass(NUMBERED_CITATION_CLASS))
    );
  }
  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register(NUMBERED_CITATION, {
      // Allow wherever text is allowed:
      allowWhere: '$text',

      // The placeholder will act as an inline node:
      isInline: true,

      // The inline widget is self-contained so it cannot be split by the caret and it can be selected:
      isObject: true,

      // The inline widget can have the same attributes as text (for example linkHref, bold).
      allowAttributesOf: '$text',

      // The placeholder can have many types, like date, name, surname, etc:
      allowAttributes: [CITATION_COUNT]
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;
    this.editor.model.schema.addAttributeCheck(context => {
      if (context.endsWith(NUMBERED_CITATION)) {
        return true;
      }
    });

    conversion.for('upcast').elementToElement({
      view: {
        name: SPAN,
        classes: [NUMBERED_CITATION_CLASS]
      },
      model: (viewElement, { writer: modelWriter }) => {
        const text = viewElement.getChild(0);
        return modelWriter.createElement(NUMBERED_CITATION, { CITATION_COUNT: text });
      }
    });

    conversion.for('upcast').elementToElement({
      view: {
        name: SPAN,
        classes: [REF]
      },
      model: (viewElement, { writer: modelWriter }) => {
        if (viewElement.hasAttribute(CLASS) && !viewElement.getAttribute(CLASS).includes(NUMBERED_CITATION_CLASS)) {
          return modelWriter.createElement(LINK_SPAN, viewElement.getAttributes());
        }

      },
      converterPriority: 'low'
    });

    conversion.for('editingDowncast').elementToElement({
      model: NUMBERED_CITATION,
      view: (modelItem, { writer: viewWriter }) => {
        removeDefaultHtmlSpanAttributes(modelItem, this.editor);
        const widgetElement = createPlaceholderView(modelItem, viewWriter);
        // Enable widget handling on a placeholder element inside the editing view.
        return toWidget(widgetElement, viewWriter);
      }
    });

    conversion.for('editingDowncast').elementToElement({
      model: SPAN,
      view: (modelItem, { writer: viewWriter }) => {
        removeDefaultHtmlSpanAttributes(modelItem, this.editor);
        const widgetElement = createPlaceholderView(modelItem, viewWriter);
        return toWidget(widgetElement, viewWriter);
      }
    });

    conversion.for('dataDowncast').elementToElement({
      model: NUMBERED_CITATION,
      view: (modelItem, { writer: viewWriter }) => {
        removeDefaultHtmlSpanAttributes(modelItem, this.editor);
        return createPlaceholderView(modelItem, viewWriter)
      }
    });

    conversion.for('dataDowncast').elementToElement({
      model: SPAN,
      view: (modelItem, { writer: viewWriter }) => createPlaceholderView(modelItem, viewWriter)
    });

    conversion.for('downcast')
      .add(dispatcher => {
        const trackChangeAttributes = [
          SUGGESTION_END_AFTER,
          SUGGESTION_START_AFTER,
          SUGGESTION_END_BEFORE,
          SUGGESTION_START_BEFORE
        ];

        /**
         * restricts creation of trackChangeAttributes values above in links in RHP
         */
        trackChangeAttributes.forEach(trackChangeAttr => {
          dispatcher.on(`attribute:${trackChangeAttr}`, (evt, data, _conversionApi) => {
            const isSpanFromRHP = [...data.item.getAncestors().reverse()].some(item =>
              item?._attrs?.get?.(CLASS) === 'num-block' ||
              item?._attrs?.get?.(CLASS)?.includes('unnum-block'))
            if (
              data.item.name === SPAN &&
              isSpanFromRHP
            ) {
              evt.stop();
            }
          }, { priority: 'highest' });
        });

        dispatcher.on(`attribute:${CITATION_COUNT}`, (evt, data, conversionApi) => {
          if (data.item.name == SPAN || data.item.name == NUMBERED_CITATION) {
            if ((data.attributeOldValue && data.attributeNewValue !== null)) {
              data.attributeNewValue = (data.attributeNewValue !== null) ? data.attributeNewValue : data.attributeOldValue;
              const myModelElement = data.item;
              conversionApi.consumable.consume(data.item, evt.name);
              const viewElement = conversionApi.mapper.toViewElement(myModelElement);
              conversionApi.writer.remove(viewElement.getChild(0));
              setContent(conversionApi.writer, data.attributeNewValue, viewElement);
            }
          }
        });
      });

    // Helper method for both downcast converters.
    function createPlaceholderView(modelItem, viewWriter) {
      const citationCount = modelItem.getAttribute(CITATION_COUNT);
      const aboutAttribute = modelItem.getAttribute('about'); // Get the 'about' attribute from the model item
      const baseHref = aboutAttribute.split('#')[0];
      const placeholderView = viewWriter.createContainerElement(SPAN, {
        class: `${REF} ${NUMBERED_CITATION_CLASS}`,
        id: modelItem.getAttribute('id')
      }, {
        isAllowedInsideAttributeElement: true
      });
      // Insert the citation count text
      const innerText = viewWriter.createText(`[${citationCount}]`);
      viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText);
      // Create child spans with specified attributes
      // Loop to create and insert each child span
      NUM_REF_POINTER_MARKUP.forEach(data => {
        const childSpan = viewWriter.createContainerElement(SPAN, {
          about: aboutAttribute,
          property: data.property,
          resource: data.resource || baseHref
        });
        // Insert the child span into the placeholder view
        viewWriter.insert(viewWriter.createPositionAt(placeholderView, placeholderView.childCount), childSpan);
      });
      return placeholderView;
    }

    function setContent(writer, citationCount, placeholderView) {
      const innerText = writer.createText(`[${citationCount}]`);
      writer.insert(writer.createPositionAt(placeholderView, 0), innerText);
    }
  }
}
